import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  catchError,
  defaultIfEmpty,
  EMPTY,
  finalize,
  first,
  from,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  zip,
} from 'rxjs';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { ToastService } from 'app/shared/services/toast.service';
import { UploadService } from 'app/shared/services/upload.service';
import { ObjectUtils } from 'app/shared/utils/object.utils';

import SketchWidget from '@arcgis/core/widgets/Sketch';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { influenceArea } from '../../default-symbols';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { typeObjectFieldName } from 'app/shared/constants/search-results.constant';
import { ExtractExpr, ItemExprUtils } from 'app/shared/utils/item-expr.utils';
import Graphic from '@arcgis/core/Graphic';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import {
  EventAttachmentModelLike,
  EventModel,
  EventModelLike,
  EventObjectModelLike,
} from 'app/shared/models/event.model';
import { EventsService } from 'app/shared/services/events.service';
import {
  geomFieldsIgnored,
  systemFieldTypesIgnored,
} from 'app/shared/constants/ignore-fields.constant';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from '@services/translation.service';

interface EventFormLike {
  subject: FormControl<string>;
  description: FormControl<string>;
}

interface SectionListTplContext<T = any> {
  title: string;
  onAddClick?: (e: any) => void;
  onDeleteClick?: (item: T, index: number) => void;
  handlePagedData: (pageIndex?: number) => void;
  columnsCount: number;
  pageObj: {
    items?: T[];
    pageIndex: number;
    totalCount?: number;
    pageSize: number;
    onPageChange?: (pageIndex: number) => void;
  };
}

interface SectionListItemLike<T = any> {
  key: string;
  text: string;
  data: T;
}

enum SelectAreaType {
  User,
  Object,
}

const entityIdFieldName = 'identidade';
const objectRegisterIdFieldName = 'idobjeto';

@Component({
  selector: 'app-events-register',
  templateUrl: 'events-register.component.html',
  styleUrls: ['events-register.component.scss'],
})
export class EventsRegisterComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('attachmentFileInputEl', { static: true })
  attachmentFileInputElRef: ElementRef<HTMLInputElement>;

  @Input()
  mapView: __esri.MapView;
  @Input()
  mainMapImageLayer: __esri.MapImageLayer;
  @Input()
  groupLayer: __esri.GroupLayer;

  @Input()
  model: EventModelLike;
  private modelInternal: EventModelLike;

  @Output()
  onSaved = new EventEmitter<EventModelLike>();

  get isEditMode() {
    return !!this.modelInternal?.id;
  }

  formGroup: FormGroup<EventFormLike>;

  private attachmentList: Array<EventAttachmentModelLike> = [];

  areaGridFields: __esri.Field[];
  areaPoint: __esri.Point;
  areaResultListToSelect: __esri.Graphic[];
  areaResultListKeysSelected: any[];
  areaResultKeyExpr: ExtractExpr<__esri.Graphic> = (item) => {
    if (!item) {
      return null;
    }

    return this.generateObjectKey(
      item.attributes[typeObjectFieldName],
      item.attributes[entityIdFieldName],
      item.attributes[objectRegisterIdFieldName]
    );
  };

  attachmentsSectionListContext: SectionListTplContext;

  private selectAreaEventsSubject = new Subject<SelectAreaType>();
  private selectAreaEventsSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private mainMapService: MainMapService,
    private uploadService: UploadService,
    private eventsService: EventsService,
    private toastService: ToastService,
    private globalLoadingController: LoadingController,
    private screenService: ScreenService,
    private ts: TranslationService
  ) {
    this.formGroup = this.formBuilder.group<EventFormLike>({
      subject: this.formBuilder.control<string>(null, [Validators.required]),
      description: this.formBuilder.control<string>(null),
    });
  }

  ngOnInit(): void {
    this.attachmentsSectionListContext =
      this.createSectionListContext<EventAttachmentModelLike>(
        () => this.attachmentList,
        (data) => {
          return { key: data.id, text: data.text, data };
        },
        {
          title: 'ANEXOS',
          pageSize: 6,
          columnsCount: 2,
          onAddClick: () => {
            this.attachmentFileInputElRef.nativeElement.click();
          },
        }
      );

    this.handleModel();

    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.handleModel(true);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.mapView ||
      changes.mainMapImageLayer ||
      changes.searchGroupLayer
    ) {
      this.handleSelectAreaEvents();
    }

    if (changes.model && !changes.model.firstChange) {
      this.handleModel();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.selectAreaEventsSubscription?.unsubscribe();

    this.attachmentsSectionListContext = null;
  }

  onBtnClearClick() {
    this.handleModel(true);
  }

  onSubmit(e) {
    e.preventDefault();

    if (this.globalLoadingController.isShown()) {
      return;
    }

    if (this.formGroup.invalid) {
      this.toastService.error(
        this.ts.getCurrentLanguage()['fill-required-fields-correctly']
      );
      return;
    }

    const model = ObjectUtils.clone(this.modelInternal) ?? new EventModel();

    const changes = this.formGroup.getRawValue();
    ObjectUtils.merge(model, changes);

    model.geometry = JSON.stringify(this.areaPoint);
    model.attachments = ObjectUtils.clone(this.attachmentList) ?? [];

    model.objects =
      this.areaResultListKeysSelected
        ?.map<EventObjectModelLike>((key) => {
          const graphic = this.areaResultListToSelect?.find((x, index) => {
            const auxKey = ItemExprUtils.extractValue(
              x,
              index,
              this.areaResultKeyExpr
            );
            return auxKey === key;
          });

          if (!graphic) {
            return null;
          }

          const type = graphic.attributes[typeObjectFieldName];
          const entityId = graphic.attributes[entityIdFieldName];
          const objectRegisterId =
            graphic.attributes[objectRegisterIdFieldName];
          return { type, entityId, objectRegisterId };
        })
        ?.filter((x) => !!x) ?? [];

    this.globalLoadingController.show();
    this.eventsService
      .create(model)
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (res) => {
          this.toastService.success(
            this.ts.getCurrentLanguage()['event-saved-successfully']
          );

          if (!this.modelInternal.id) {
            this.modelInternal = new EventModel();
            this.handleModel();
          }

          this.onSaved.emit(res);
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-save-event']
          );
        },
      });
  }

  onAttachmentFileChange() {
    if (this.globalLoadingController.isShown()) {
      return;
    }

    const file = this.attachmentFileInputElRef.nativeElement.files?.item(0);
    if (file) {
      this.attachmentFileInputElRef.nativeElement.value = '';

      this.globalLoadingController.show();
      this.uploadService
        .uploadFile(file)
        .pipe(
          finalize(() => {
            this.globalLoadingController.hide();
          }),
          takeUntil(this.lifecycle)
        )
        .subscribe({
          next: (url) => {
            this.attachmentList.push({
              text: file.name,
              url,
            });

            this.attachmentsSectionListContext?.handlePagedData();

            this.toastService.success(
              this.ts.getCurrentLanguage()['upload-completed-successfully']
            );
          },
          error: (response) => {
            if (!response) {
              return;
            }

            this.toastService.error(
              this.ts.getCurrentLanguage()['failed-upload-file']
            );
          },
        });
    }
  }

  onBtnSelectGeometryInMapClick() {
    this.enableSelectAreaEvents();
  }

  private handleModel(clearMode = false) {
    if (!clearMode) {
      this.modelInternal = ObjectUtils.clone(this.model) ?? new EventModel();
    } else {
      this.modelInternal = new EventModel({
        id: this.model?.id,
      });
    }

    const values = this.modelInternal ?? {};

    this.formGroup.reset(values);

    this.areaPoint = EsriUtils.geometryFromJson(
      this.modelInternal.geometry
    ) as __esri.Point;

    this.attachmentList = this.modelInternal?.attachments?.slice() ?? [];
    this.attachmentsSectionListContext?.handlePagedData();

    this.areaResultListKeysSelected =
      this.modelInternal?.objects?.map((x) => {
        return this.generateObjectKey(x.type, x.entityId, x.objectRegisterId);
      }) ?? [];

    this.selectAreaEventsSubject.next(SelectAreaType.Object);
  }

  private enableSelectAreaEvents() {
    this.selectAreaEventsSubject.next(SelectAreaType.User);
  }

  private handleSelectAreaEvents() {
    this.selectAreaEventsSubscription?.unsubscribe();
    if (!this.mapView || !this.mainMapImageLayer) {
      return;
    }

    const { mapView, mainMapImageLayer } = this;

    const subscription = new Subscription();

    const searchGroupLayer: __esri.LayersMixin = this.groupLayer ?? mapView.map;

    const highlightGroupLayer = new GroupLayer();
    searchGroupLayer.add(highlightGroupLayer);

    const graphicsLayer = new GraphicsLayer();
    searchGroupLayer.add(graphicsLayer);

    const drawingLayer = new GraphicsLayer();
    searchGroupLayer.add(drawingLayer);

    const sketch = new SketchWidget({
      view: this.mapView,
      layer: drawingLayer,
      creationMode: 'single',
    });

    subscription.add(
      EsriUtils.loadAllLayers(mainMapImageLayer).subscribe(() => {
        const layers = EsriUtils.getDeeperLayers(
          mainMapImageLayer
        ) as __esri.Sublayer[];

        const fieldList: __esri.Field[] = [];
        const fieldsSet = new Set<string>();

        layers.forEach((layer) => {
          const fields = layer.fields;
          fields.forEach((field) => {
            if (
              !fieldsSet.has(field.name) &&
              !geomFieldsIgnored.includes(field.name) &&
              !systemFieldTypesIgnored.includes(field.type)
            ) {
              fieldsSet.add(field.name);
              fieldList.push(field);
            }
          });
        });

        this.areaGridFields = fieldList;
      })
    );

    subscription.add(
      this.selectAreaEventsSubject
        .pipe(
          switchMap((type) => {
            let graphicObservable: Observable<__esri.Graphic>;
            switch (type) {
              case SelectAreaType.Object:
                if (this.areaPoint) {
                  graphicObservable = of(
                    new Graphic({ geometry: this.areaPoint })
                  );
                } else {
                  graphicObservable = EMPTY;
                }
                break;
              case SelectAreaType.User:
              default:
                sketch.create('point');

                graphicObservable = EsriUtils.on(sketch, 'create').pipe(
                  finalize(() => {
                    sketch.cancel();
                  }),
                  first((evt: __esri.SketchCreateEvent) => {
                    switch (evt.state) {
                      case 'cancel':
                        graphicsLayer.removeAll();
                        break;
                      case 'complete':
                        return true;
                    }

                    return false;
                  }),
                  map((e) => e.graphic),
                  tap((graphic) => {
                    this.areaPoint = graphic.geometry as __esri.Point;

                    this.areaResultListKeysSelected = [];
                  })
                );
                break;
            }

            return graphicObservable.pipe(
              map((graphic) => {
                const geometry = EsriUtils.geodesicBuffer(graphic.geometry, 50);
                graphic.geometry = geometry;

                graphic.symbol = influenceArea.polygonFillSymbol;
                graphicsLayer.add(graphic);

                return graphic.geometry;
              }),
              switchMap((geometry) => {
                if (!geometry) {
                  return EMPTY;
                }

                return new Observable((subscriber) => {
                  let sublayerList = EsriUtils.getDeeperLayers([
                    mainMapImageLayer,
                  ]) as __esri.Sublayer[];

                  if (this.modelInternal?.objects?.length) {
                    sublayerList = sublayerList.filter((sublayer) =>
                      this.modelInternal.objects.some(
                        (x) => x.type === sublayer.title
                      )
                    );
                  }

                  const layersObservable = zip(
                    sublayerList.reverse().map((sublayer) => {
                      const query = sublayer.createQuery();
                      query.where = '1=1';

                      if (this.modelInternal?.objects?.length) {
                        query.where += ` and (${this.generateWhereClauseFromObjects(
                          this.modelInternal.objects
                        )})`;
                      }

                      query.geometry = geometry;
                      query.outFields = ['*'];
                      query.returnGeometry = true;

                      return EsriUtils.queryFeatures(sublayer, query).pipe(
                        mergeMap((featureSet) => {
                          if (!featureSet?.features?.length) {
                            return of(null);
                          }

                          const attributesWithDomain = featureSet.fields.filter(
                            (x) => x.domain
                          );
                          EsriUtils.handleFieldDomains(
                            featureSet.features,
                            attributesWithDomain
                          );

                          return of(
                            new FeatureLayer({
                              objectIdField: sublayer.objectIdField,
                              renderer: sublayer.renderer,
                              fields: featureSet.fields,
                              source: featureSet.features,
                            })
                          );
                        })
                      );
                    })
                  ).pipe(
                    map((layers) => layers.filter((x) => !!x)),
                    defaultIfEmpty([] as __esri.FeatureLayer[])
                  );

                  this.globalLoadingController.show();
                  subscriber.add(
                    layersObservable
                      .pipe(
                        catchError(() => {
                          this.toastService.error(
                            this.ts.getCurrentLanguage()['failed-load-data']
                          );
                          return EMPTY;
                        }),
                        finalize(() => {
                          this.globalLoadingController.hide();
                        }),
                        mergeMap((layers) => {
                          this.areaResultListToSelect = layers
                            .reverse()
                            .flatMap((layer) => layer.source.toArray());

                          if (!layers.length) {
                            return EMPTY;
                          }

                          highlightGroupLayer.addMany(layers);

                          this.globalLoadingController.show();
                          return zip(
                            layers.map((layer) =>
                              from(mapView.whenLayerView(layer))
                            )
                          ).pipe(
                            finalize(() => {
                              this.globalLoadingController.hide();
                            }),
                            mergeMap((layerViews) => {
                              return new Observable((subscriber) => {
                                subscriber.add(
                                  zip(
                                    layerViews.map((layerView, index) => {
                                      const layer = layers[index];

                                      return EsriUtils.highlight(
                                        layerView,
                                        layer.source.toArray()
                                      );
                                    })
                                  ).subscribe()
                                );

                                subscriber.add(() => {
                                  highlightGroupLayer.removeAll();
                                });
                              });
                            })
                          );
                        })
                      )
                      .subscribe()
                  );

                  subscriber.add(() => {
                    graphicsLayer.removeAll();

                    this.areaResultListToSelect = null;
                    this.areaResultListKeysSelected = [];
                    this.areaPoint = null;
                  });
                });
              })
            );
          })
        )
        .subscribe()
    );

    subscription.add(() => {
      searchGroupLayer.remove(highlightGroupLayer);
      highlightGroupLayer.destroy();

      searchGroupLayer.remove(graphicsLayer);
      graphicsLayer.destroy();

      searchGroupLayer.remove(drawingLayer);
      drawingLayer.destroy();

      sketch.cancel();
      sketch.destroy();
    });

    this.selectAreaEventsSubscription = subscription;
  }

  private createSectionListContext<T = any>(
    itemsFn: () => T[],
    mapItemsFn: (item: T) => SectionListItemLike<T>,
    cfg: {
      title: string;
      onAddClick?: (handlePagedData: (pageIndex?: number) => void) => void;
      onDeleteClick?: (
        item: T,
        index: number,
        handlePagedData: (pageIndex?: number) => void
      ) => void;
      pageSize: number;
      columnsCount?: number;
    }
  ): SectionListTplContext<SectionListItemLike<T>> {
    let _pagedData: any[];
    let _pageIndex: number = 0;
    let _totalCount: number;
    const pageSize = cfg.pageSize;
    const columnsCount = cfg.columnsCount ?? 1;

    const handlePagedData = (pageIndex: number = _pageIndex) => {
      const list = itemsFn();
      _totalCount = list?.length;

      const lastPageIndex =
        Math.ceil((list?.length ?? 0) / cfg.pageSize) - 1 || 0;
      const newPageIndex =
        lastPageIndex <= -1
          ? -1
          : Math.max(0, Math.min(pageIndex, lastPageIndex));
      _pageIndex = newPageIndex;

      const start = newPageIndex * cfg.pageSize;

      _pagedData = list?.slice(start, start + cfg.pageSize) ?? [];

      if (mapItemsFn) {
        _pagedData = _pagedData?.map(mapItemsFn);
      }
    };

    const onDeleteClick =
      cfg.onDeleteClick ??
      ((data, index, handlePagedData) => {
        const list = itemsFn();
        list?.splice(index, 1);

        handlePagedData();
      });

    return {
      title: cfg.title,
      onAddClick: cfg.onAddClick?.bind(cfg, handlePagedData),
      onDeleteClick: (item, index) => {
        const data = item.data;
        index += _pageIndex * pageSize;

        onDeleteClick?.(data, index, handlePagedData);
      },
      handlePagedData,
      columnsCount,
      pageObj: {
        get items() {
          if (!_pagedData) {
            handlePagedData(0);
          }

          return _pagedData;
        },
        get pageIndex() {
          return _pageIndex;
        },
        get totalCount() {
          return _totalCount;
        },
        pageSize,
        onPageChange: handlePagedData,
      },
    };
  }

  private generateWhereClauseFromObjects(
    objects: EventObjectModelLike[]
  ): string {
    return objects
      .map((item) => {
        return `(${entityIdFieldName}='${item.entityId}' and ${objectRegisterIdFieldName}='${item.objectRegisterId}')`;
      })
      .join(' or ');
  }

  private generateObjectKey(
    type: string,
    entityId: string,
    objectRegisterId: string
  ) {
    return [type, entityId, objectRegisterId].join('_');
  }
}
